import { useEffect, useState } from "react";
import { Badge, Button, ModalShell } from "@vb/ui";
import { verifyRound, hashServerSeed } from "@vb/engine";
import { rgs } from "../lib/rgs-client.js";
import { truncateHash } from "../lib/format.js";

interface ProvablyFairModalProps {
  open: boolean;
  onClose: () => void;
  roundId: string | null;
  serverSeedHash: string | null;
}

interface SeedData {
  roundId: string;
  serverSeed: string;
  serverSeedHash: string;
  clientSeed: string;
  nonce: number;
  bustMultiplier: number;
  revealed: boolean;
}

export function ProvablyFairModal({
  open,
  onClose,
  roundId,
  serverSeedHash,
}: ProvablyFairModalProps) {
  const [seeds, setSeeds] = useState<SeedData | null>(null);
  const [verification, setVerification] = useState<"idle" | "ok" | "fail">("idle");

  useEffect(() => {
    if (!open || !roundId) return;
    let cancelled = false;
    rgs
      .seeds(roundId)
      .then((r) => {
        if (!cancelled) {
          setSeeds(r);
          setVerification("idle");
        }
      })
      .catch(() => {
        if (!cancelled) setSeeds(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, roundId]);

  const handleVerify = () => {
    if (!seeds || !seeds.revealed) return;
    // For arbitrary historical rounds, the canonical "claimed hash" is the one
    // returned alongside the seeds — not the live session's hash. Falls back to
    // the prop only when seeds didn't supply one (shouldn't happen in practice).
    const claimedHash = seeds.serverSeedHash || serverSeedHash;
    if (!claimedHash) return;
    const v = verifyRound({
      serverSeed: seeds.serverSeed,
      clientSeed: seeds.clientSeed,
      nonce: seeds.nonce,
      claimedBustMultiplier: seeds.bustMultiplier,
      claimedServerSeedHash: claimedHash,
    });
    setVerification(v.valid ? "ok" : "fail");
  };

  const rows: Array<[string, string]> = [
    ["round", roundId ?? "—"],
    ["server seed hash", seeds?.serverSeedHash ? truncateHash(seeds.serverSeedHash, 8, 4) : truncateHash(serverSeedHash ?? "", 8, 4)],
    [
      "server seed",
      seeds?.revealed ? truncateHash(seeds.serverSeed, 8, 4) : "locked (reveals on resolve)",
    ],
    ["client seed", seeds?.clientSeed ?? "—"],
    ["nonce", seeds ? String(seeds.nonce).padStart(5, "0") : "—"],
    [
      "recomputed hash",
      seeds?.revealed ? truncateHash(hashServerSeed(seeds.serverSeed), 8, 4) : "—",
    ],
    [
      "result",
      seeds?.revealed
        ? `${seeds.bustMultiplier.toFixed(2)}×`
        : seeds
        ? "locked (reveals on resolve)"
        : "—",
    ],
  ];

  return (
    <ModalShell open={open} onClose={onClose} labelledBy="pf-title">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 id="pf-title" style={{ margin: 0 }}>
          Provably Fair
        </h2>
        {verification === "ok" ? (
          <Badge kind="fair">VERIFIED</Badge>
        ) : verification === "fail" ? (
          <Badge kind="bust">MISMATCH</Badge>
        ) : (
          <Badge kind="fair">READY</Badge>
        )}
      </div>
      <p style={{ fontSize: 13, color: "var(--fg-2)", margin: 0 }}>
        Each round commits a server seed hash before play. After the round, the seed is revealed —
        anyone can reproduce the result in the browser with the engine shipped client-side.
      </p>
      {!roundId ? (
        <div
          style={{
            padding: "10px 14px",
            border: "1px solid var(--line-strong)",
            background: "var(--bg-2)",
            borderRadius: 10,
            fontSize: 12,
            color: "var(--fg-2)",
            lineHeight: 1.5,
          }}
        >
          Play a round first. Seed details appear here once the round resolves.
        </div>
      ) : null}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 0,
          background: "var(--bg-2)",
          border: "1px solid var(--line)",
          borderRadius: 10,
          padding: "4px 14px",
        }}
      >
        {rows.map(([k, v]) => (
          <div
            key={k}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "10px 0",
              borderBottom: "1px solid var(--line)",
              fontFamily: "var(--font-mono)",
              fontSize: 12,
            }}
          >
            <span style={{ color: "var(--fg-3)" }}>{k}</span>
            <span
              style={{
                color: k === "result" ? "var(--gold-bright)" : "var(--fg-1)",
                textAlign: "right",
              }}
            >
              {v}
            </span>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <Button onClick={onClose} style={{ flex: 1 }}>
          Close
        </Button>
        <Button
          onClick={handleVerify}
          disabled={!seeds?.revealed}
          style={{ flex: 1 }}
        >
          {seeds?.revealed ? "Verify in browser" : "Awaiting reveal"}
        </Button>
      </div>
    </ModalShell>
  );
}
